import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
	PeriodosLabel,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';

import { Validators } from '@angular/forms';
import { optionsChartPluviometriaObservada } from '@home/submodulos/dados-meteorologicos/componentes/relatorios/grafico-pluviometria-observada/chartOptions';
import {
	AgrupamentoResponse,
	DadosAgrupamento,
} from '@home/submodulos/dados-meteorologicos/interfaces/agrupamento';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import * as Highcharts from 'highcharts/highstock';
import { Subject, takeUntil } from 'rxjs';
import { corrigeDuplicacaoNome, gerarFilenameGrafico } from '../../../utils';
import { DateTimeUtils } from '@utils/datetime-util';
import { Select } from '@layout/interfaces/select';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import {
	alinharDado,
	baixarEAbrirPDF,
	criarImagemBase64FromChart,
	enumAsSelectOptions,
	isNuloOuUndefined,
	numberToBrNumberTables,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import * as pdfseira from '@utils/pdf-seira';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { DataModel } from 'mr-excel';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import {
	AgrupamentoEspecial,
	PostosRelatorios,
} from '@home/submodulos/dados-meteorologicos/interfaces';
import { format } from 'date-fns';
import { NumberUtils } from '@utils/number-utils';
import {
	getCorPorRegiao,
	ordenarRegioesPluviometricas,
} from '../../../utils/grafico-regioes-utils';
import { COR_REGIAO_INDIVIDUAL, obterLabelPeriodo } from '../../../utils';
import { isEstadoCompleto } from '../../../utils/agrupamento-utils';

@Component({
	selector: 'seira-grafico-pluviometria-observada',
	templateUrl: './grafico-pluviometria-observada.component.html',
	styleUrls: ['./grafico-pluviometria-observada.component.scss'],
})
export class GraficoPluviometriaObservadaComponent
	implements OnInit, OnDestroy, InstanciaRelatorio
{
	inputs = inject(INPUTS_RELATORIOS);
	chart: Highcharts.Chart;
	chartOptions: Highcharts.Options = optionsChartPluviometriaObservada;
	_destroyed = new Subject();
	descricaoRelatorio =
		'Define-se como pluviometria a quantidade total de chuva registrada em um local ou região específica, medida ao longo de um período.';
	periodoTitulo = '';
	loading = true;
	response: AgrupamentoResponse[];
	agrupamento: Select[] | any = [];
	grafico?: Highcharts.Chart;
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: () => this.exportarPNG(),
		},
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => {
				return this.exportarPDF();
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	tituloAgrupamento = '';
	postos: PostosRelatorios[] = [];

	get _agrupamento(): string {
		return this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value;
	}

	constructor(private relatoriosService: RelatoriosService) {
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
	}

	ngOnInit(): void {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.agrupamento[0].value);
		this.inputs.form
			.get(FormularioRelatorio.MICRORREGIAO)
			?.setValue(this.inputs.microrregioes[0].value);

		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setValidators();
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MESORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.BACIA)?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.clearValidators();
		this._destroyed.next(undefined);
	}

	setValidatorsByAgrupamento(agrupamentoValue: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MUNICIPIO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.ESTACAO);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.MICRORREGIAO
		);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MESORREGIAO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.BACIA);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		);

		switch (agrupamentoValue) {
			case 'MUNICIPIO_POSTO':
				this.inputs.form
					?.get(FormularioRelatorio.ESTACAO)
					?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValue('0', { emitEvent: false });
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MESORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MESORREGIAO)
					?.setValue('0', { emitEvent: false });
				this.inputs.form
					?.get(FormularioRelatorio.MESORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'BACIA_SUBBACIA':
				this.inputs.form
					?.get(FormularioRelatorio.BACIA)
					?.setValue('0', { emitEvent: false });
				this.inputs.form
					?.get(FormularioRelatorio.BACIA)
					?.setValidators(Validators.required);
				break;
			case 'MUNICIPIO':
				this.inputs.form
					?.get(FormularioRelatorio.MUNICIPIO)
					?.setValidators(Validators.required);
				break;
			case 'REGIAO_PLUVIOMETRICA':
				this.inputs.form
					?.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
					?.setValue('0', { emitEvent: false });
				this.inputs.form
					?.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
					?.setValidators(Validators.required);
				break;
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByAgrupamento(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	get isMesorregiaoEstadoCompleto() {
		return (
			this._agrupamento === 'MESORREGIAO' &&
			this.inputs.form.get(FormularioRelatorio.MESORREGIAO)?.value === '0'
		);
	}

	get isMicrorregiaoEstadoCompleto() {
		return (
			this._agrupamento === 'MICRORREGIAO' &&
			this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.value === '0'
		);
	}

	get isBaciaSubBaciaEstadoCompleto() {
		return (
			this._agrupamento === 'BACIA_SUBBACIA' &&
			this.inputs.form.get(FormularioRelatorio.BACIA)?.value === '0'
		);
	}

	setChart(chart: Highcharts.Chart) {
		this.chart = chart;

		const larguraAtual = this.chart.chartWidth;
		const alturaAtual = this.chart.chartHeight;

		this.chartOptions = {
			...this.chartOptions,
			exporting: {
				...this.chartOptions.exporting,
				enabled: false, // 🔹 desativa o menu (botão sanduíche)
				sourceWidth: larguraAtual,
				sourceHeight: alturaAtual,
				scale: 1,
				chartOptions: {
					chart: {
						width: larguraAtual,
						height: alturaAtual,
					},
				},
			},
		};

		this.chart.update(this.chartOptions);
		this.configurarNewGrafico(this.response);
	}
	checaAgrupamento(dados: DadosAgrupamento) {
		const agrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;

		switch (agrupamento) {
			case 'MUNICIPIO_POSTO':
				dados.posto = this.inputs.form.get(FormularioRelatorio.ESTACAO)?.value;
				break;

			case 'MICRORREGIAO':
				const microrregiao = this.inputs.form.get(
					FormularioRelatorio.MICRORREGIAO
				)?.value;
				dados.microrregiao = microrregiao === '0' ? undefined : microrregiao;
				break;
			case 'MESORREGIAO':
				const mesorregiao = this.inputs.form.get(
					FormularioRelatorio.MESORREGIAO
				)?.value;
				dados.mesorregiao = mesorregiao === '0' ? undefined : mesorregiao;
				break;
			case 'BACIA_SUBBACIA':
				const bacia = this.inputs.form.get(FormularioRelatorio.BACIA)?.value;
				dados.bacia = bacia === '0' ? undefined : bacia;
				break;
			case 'MUNICIPIO':
				dados.municipio = this.inputs.form.get(
					FormularioRelatorio.MUNICIPIO
				)?.value;
				break;
			case 'REGIAO_PLUVIOMETRICA':
				const regiao = this.inputs.form.get(
					FormularioRelatorio.REGIAO_PLUVIOMETRICA
				)?.value;
				dados.regiao = regiao === '0' ? undefined : regiao;
				break;
		}
	}

	gerarRelatorio() {
		const diaInicio = this.inputs.form.get(
			FormularioRelatorio.DATA_INICIO
		)?.value;
		const diaFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		const periodoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;
		const agrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;

		if (
			!diaInicio ||
			!diaFim ||
			!agrupamento ||
			!periodoAgrupamento ||
			this.inputs.form.invalid
		) {
			return;
		}

		const dados: DadosAgrupamento = {
			diaInicio,
			diaFim,
			agrupamento,
			periodoAgrupamento: periodoAgrupamento.toUpperCase(),
		};
		this.checaAgrupamento(dados);
		this.loading = true;
		this.inputs.setLoading(true);
		this.relatoriosService.buscarRelatorioPorAgrupamento(dados).subscribe({
			next: preciptacoesAgrupadas => {
				this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
					preciptacoesAgrupadas
				);
				this.response = preciptacoesAgrupadas;
				const periodo = this.inputs.form.get(
					FormularioRelatorio.PERIODO_BUSCA
				)?.value;
				const categories = this.extrairDatasPrecipitacao(
					this.response,
					periodo
				);

				const periodoFormatado = DateTimeUtils.formatarDataPeriodo(
					diaInicio,
					diaFim,
					periodoAgrupamento
				);
				const labelPeriodo = obterLabelPeriodo(periodoAgrupamento);
				const tituloGrafico = `Pluviometria (mm) - ${labelPeriodo} (${periodoFormatado})`;

				this.periodoTitulo = tituloGrafico;

				this.chartOptions = {
					...this.chartOptions,
					title: {
						...this.chartOptions.title,
						text: tituloGrafico,
					},
					exporting: {
						...this.chartOptions.exporting,
						filename: gerarFilenameGrafico(
							`grafico_pluviometria_observada_${this.inputs.form
								.get(FormularioRelatorio.AGRUPAMENTO)
								?.value.toLocaleLowerCase()}_${periodoAgrupamento.toLocaleLowerCase()}`
						),
					},
					xAxis: [categories as Highcharts.XAxisOptions],
				};
			},
			complete: () => {
				this.loading = false;
				this.inputs.setLoading(false);
			},
		});
	}

	extrairDatasPrecipitacao(
		preciptacoesAgrupadas: AgrupamentoResponse[],
		periodoAgrupamento: PeriodosLabel
	) {
		return [
			...new Set(
				preciptacoesAgrupadas
					.map(precipitacoes => {
						/**
						 * Esse regex é necessário por questões de algum bug que acontece no backend
						 * onde a data é retornada como "2024.0" por exemplo.
						 * Esse regex é uma solução provisória para resolver esse bug.
						 */
						let data = new Date(precipitacoes.data!.replace(/\.0/g, ''));
						data = new Date(data.setHours(data.getHours() + 3));
						return periodoAgrupamento.toUpperCase() === 'ANUAL' ||
							periodoAgrupamento.toUpperCase() === 'ANUAL_PARCIAL'
							? format(data, 'yyyy')
							: periodoAgrupamento.toUpperCase() === 'MENSAL'
								? format(data, 'MM/yyyy')
								: format(data, 'dd/MM/yyyy');
					})
					.sort((a, b) => +a - +b)
			),
		];
	}

	extrairNomes(preciptacoesAgrupadas: AgrupamentoResponse[]) {
		return [
			...new Set(
				preciptacoesAgrupadas.map(el => corrigeDuplicacaoNome(el.nome)).sort()
			),
		];
	}

	private getLabelAgrupamento(agrupamento: string | undefined) {
		if (this.isMesorregiaoEstadoCompleto) {
			return 'Estado completo';
		}

		if (this.isMicrorregiaoEstadoCompleto) {
			return 'Estado completo';
		}

		return AgrupamentoEspecial[agrupamento as keyof typeof AgrupamentoEspecial];
	}

	private getCorSerie(nomeSerie: string): string | undefined {
		if (!isEstadoCompleto(this.inputs.form, this._agrupamento)) {
			return COR_REGIAO_INDIVIDUAL;
		}
		if (this._agrupamento === 'MESORREGIAO') {
			return getCorPorRegiao(
				nomeSerie,
				this.inputs.mesorregioes,
				this._agrupamento
			);
		}
		if (this._agrupamento === 'REGIAO_PLUVIOMETRICA') {
			return getCorPorRegiao(nomeSerie, this.inputs.regioes, this._agrupamento);
		}

		return undefined;
	}

	configurarNewGrafico(preciptacoesAgrupadas: AgrupamentoResponse[]) {
		const periodo = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		let extractedSeries = this.extrairNomes(preciptacoesAgrupadas);
		const categories = this.extrairDatasPrecipitacao(
			preciptacoesAgrupadas,
			periodo
		);

		if (this._agrupamento === 'REGIAO_PLUVIOMETRICA') {
			extractedSeries = ordenarRegioesPluviometricas(extractedSeries);
		}

		this.chart.xAxis[0].setCategories(categories);
		const series = extractedSeries.map(serie => ({
			name: corrigeDuplicacaoNome(serie),
			color: this.getCorSerie(corrigeDuplicacaoNome(serie)),
			data: categories.map(categoria => {
				const pluviometriaFindedByName = preciptacoesAgrupadas.find(
					agrupamento => {
						/**
						 * Esse regex é necessário por questões de algum bug que acontece no backend
						 * onde a data é retornada como "2024.0" por exemplo.
						 * Esse regex é uma solução provisória para resolver esse bug.
						 */
						let data = new Date(agrupamento.data!.replace(/\.0/g, ''));
						data = new Date(data.setHours(data.getHours() + 3));
						const dataFormatted =
							periodo.toUpperCase() === 'ANUAL' ||
							periodo.toUpperCase() === 'ANUAL_PARCIAL'
								? format(data, 'yyyy')
								: periodo.toUpperCase() === 'MENSAL'
									? format(data, 'MM/yyyy')
									: format(data, 'dd/MM/yyyy');
						return (
							dataFormatted.replace(/\.0/g, '') === categoria &&
							corrigeDuplicacaoNome(agrupamento.nome) === serie
						);
					}
				);
				return isNuloOuUndefined(pluviometriaFindedByName)
					? null
					: {
							name: corrigeDuplicacaoNome(pluviometriaFindedByName.nome),
							y:
								pluviometriaFindedByName?.precipitacaoAcumulada != null
									? +pluviometriaFindedByName.precipitacaoAcumulada.toFixed(2)
									: null,
						};
			}),
		}));
		series.forEach(serie => this.chart.addSeries(serie as any));
	}

	getDadosTabelaParaExportacao(dados: AgrupamentoResponse[], isPdf: boolean) {
		const tableData: any[][] = [];
		const colunas: any[] = [];
		const agrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;

		colunas.push({
			text: this.getLabelAgrupamento(agrupamento),
			fillColor: '#DCDCDC',
			alignment: 'center',
		});

		const periodoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		colunas.push(
			{
				text: 'Pluviometria observada (mm)',
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{
				text: 'Data',
				fillColor: '#DCDCDC',
				alignment: 'center',
			}
		);

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		dados.forEach((item: AgrupamentoResponse) => {
			const rowData = [];

			rowData.push(
				agrupamento === 'MUNICIPIO_POSTO'
					? item.nome && corrigeDuplicacaoNome(item.nome)
					: alinharDado(item.nome && corrigeDuplicacaoNome(item.nome), isPdf),
				alinharDado(
					numberToBrNumberTables(item.precipitacaoAcumulada, 1),
					isPdf
				),
				alinharDado(
					item.data && periodoAgrupamento === 'diario'
						? DateTimeUtils.formatarData(
								DateTimeUtils.formatToZonedDateTime(item.data),
								'dd/MM/yyyy'
							)
						: item.data && periodoAgrupamento === 'mensal'
							? DateTimeUtils.formatarData(
									DateTimeUtils.formatToZonedDateTime(item.data),
									'MM/yyyy'
								)
							: item.data?.substring(0, 4),
					isPdf
				)
			);

			tableData.push(rowData);
		});

		return tableData;
	}

	retornarPluviometriaObservadaParaXLSX() {
		const tableData: any[] = [];
		const periodo = this.inputs.form.get('periodoBusca')?.value;

		this.response.map(resp => {
			tableData.push({
				nome: resp.nome && corrigeDuplicacaoNome(resp.nome),
				precipitacaoAcumulada: isNuloOuUndefined(resp.precipitacaoAcumulada)
					? '-'
					: NumberUtils.getIgnorandoCasasDecimais(resp.precipitacaoAcumulada),
				data:
					resp.data && periodo === 'diario'
						? DateTimeUtils.formatarData(resp.data, 'dd/MM/yyyy')
						: resp.data && periodo === 'mensal'
							? DateTimeUtils.formatarData(resp.data, 'MM/yyyy')
							: Number(resp.data?.substring(0, 4)),
			});
		});

		return tableData;
	}

	getXlsxHeaders(): Array<{ label: string; text: string; size: number }> {
		const headers: Array<{ label: string; text: string; size: number }> = [];
		const agrupamento = this.inputs.form.get('agrupamento')?.value;
		const periodoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;

		headers.push(
			{
				label: 'nome',
				text: this.getLabelAgrupamento(agrupamento),
				size: 24,
			},
			{
				label: 'precipitacaoAcumulada',
				text: 'Pluviometria observada (mm)',
				size: 24,
			},
			{
				label: 'data',
				text: 'Data',
				size: 24,
			}
		);

		return headers;
	}

	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = err => reject(err);
			img.src = src;
		});
	}

	private async getLogoAesaBase64(): Promise<string> {
		const carregarImagem = (src: string): Promise<HTMLImageElement> => {
			return new Promise((resolve, reject) => {
				const img = new Image();
				img.crossOrigin = 'anonymous';
				img.onload = () => resolve(img);
				img.onerror = err => reject(err);
				img.src = src;
			});
		};

		const logo = await carregarImagem('assets/images/logos/logoAesa.svg');

		const canvas = document.createElement('canvas');
		canvas.width = logo.width;
		canvas.height = logo.height;

		const ctx = canvas.getContext('2d')!;
		ctx.fillStyle = '#FFFFFF';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(logo, 0, 0, canvas.width, canvas.height);

		return canvas.toDataURL('image/png', 1);
	}

	private async getLogoGovernoBase64(): Promise<string> {
		const carregarImagem = (src: string): Promise<HTMLImageElement> => {
			return new Promise((resolve, reject) => {
				const img = new Image();
				img.crossOrigin = 'anonymous';
				img.onload = () => resolve(img);
				img.onerror = err => reject(err);
				img.src = src;
			});
		};

		const logo = await carregarImagem('assets/images/logos/governo-pb.png');

		const canvas = document.createElement('canvas');
		canvas.width = logo.width;
		canvas.height = logo.height;

		const ctx = canvas.getContext('2d')!;
		ctx.fillStyle = '#FFFFFF';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(logo, 0, 0, canvas.width, canvas.height);

		return canvas.toDataURL('image/png', 1);
	}

	async exportarPDF() {
		const chartsConverted: TDocumentDefinitions['content'] = [];
		const agrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;

		const imagemGrafico = await criarImagemBase64FromChart(this.chart);

		chartsConverted.push({
			image: imagemGrafico,
			fontSize: 10,
			alignment: 'center',
			height: 400,
			width: 750,
		});

		const documentDefinition: any =
			await pdfseira.documentDefinitions('landscape');

		documentDefinition.pageOrientation = 'landscape';

		const logoAesaBase64 = await this.getLogoAesaBase64();

		documentDefinition.footer = (currentPage: number, pageCount: number) => {
			return {
				margin: [40, 0, 40, 10],
				columns: [
					{
						text: `Página ${currentPage} de ${pageCount}`,
						alignment: 'left',
						fontSize: 8,
					},
					{
						image: logoAesaBase64,
						width: 60,
						alignment: 'right',
					},
				],
			};
		};

		const content = [
			{
				text: '',
				margin: [15, 15, 5, 15],
			},
			...chartsConverted,
		];

		documentDefinition.content.push(content);

		const tableData = this.getDadosTabelaParaExportacao(this.response, true);

		documentDefinition.content.push(
			{
				text: '',
				alignment: 'center',
				fontSize: 10,
			},
			{
				table: {
					widths: ['35%', '35%', '30%'],
					body: tableData,
					fontSize: agrupamento != 'MUNICIPIO_POSTO' ? 12 : 10,
				},
				marginTop: 100,
			}
		);

		baixarEAbrirPDF(this.periodoTitulo, documentDefinition);
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(this.response, false);
		DocumentExporter.gerarCSV(tableData, this.periodoTitulo);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(this.response, false);
		let txtData = '';

		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(txtData, this.periodoTitulo);
	}

	exportarXLSX() {
		const agrupamentoAtual = this._agrupamento;
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as DataModel.AlignmentOption,
		};
		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as DataModel.AlignmentOption,
		};
		const headers = this.getXlsxHeaders();
		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number
					) {
						if (data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex === 0 && agrupamentoAtual === 'MUNICIPIO_POSTO') {
								return 'rowStyle';
							} else {
								return 'rowStyleCenter';
							}
						}
					},
					headers: headers,
					data: this.retornarPluviometriaObservadaParaXLSX(),
					columns: [],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: this.periodoTitulo,
						styleId: 'title',
					},
				},
			],
			fileName: this.periodoTitulo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	private downloadDataUrl(nomeArquivo: string, dataUrl: string) {
		const link = document.createElement('a');
		link.href = dataUrl;
		link.download = nomeArquivo;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	async exportarPNG() {
		if (!this.chart) {
			console.error(
				'[PLUVIOMETRIA PNG] Não é possível exportar PNG pois nenhum gráfico foi gerado.'
			);
			return;
		}

		try {
			const chartBase64 = await criarImagemBase64FromChart(this.chart);
			const baseImg = await this.carregarImagem(chartBase64 as string);

			const canvas = document.createElement('canvas');
			canvas.width = baseImg.width;
			canvas.height = baseImg.height;

			const ctx = canvas.getContext('2d')!;
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);

			try {
				const margem = canvas.width * 0.02;

				const larguraLogoAesa = canvas.width * 0.08;
				const logoAesa = await this.carregarImagem(
					'assets/images/logos/logoAesa.svg'
				);
				const alturaLogoAesa =
					(larguraLogoAesa / logoAesa.width) * logoAesa.height;

				const xLogoAesa = margem;
				const yLogoAesa = canvas.height - alturaLogoAesa - margem;

				ctx.drawImage(
					logoAesa,
					xLogoAesa,
					yLogoAesa,
					larguraLogoAesa,
					alturaLogoAesa
				);

				const larguraLogoGoverno = canvas.width * 0.1;
				const logoGoverno = await this.carregarImagem(
					'assets/images/logos/governo_pb.png'
				);
				const alturaLogoGoverno =
					(larguraLogoGoverno / logoGoverno.width) * logoGoverno.height;

				const xLogoGoverno = canvas.width - larguraLogoGoverno - margem;
				const yLogoGoverno = canvas.height - alturaLogoGoverno - margem;

				ctx.drawImage(
					logoGoverno,
					xLogoGoverno,
					yLogoGoverno,
					larguraLogoGoverno,
					alturaLogoGoverno
				);
			} catch (e) {
				console.error('[PLUVIOMETRIA PNG] Erro ao desenhar as logos:', e);
			}

			const dataUrl = canvas.toDataURL('image/png', 1);

			const agrupamento = this.inputs.form.get(
				FormularioRelatorio.AGRUPAMENTO
			)?.value;
			const periodoAgrupamento = this.inputs.form.get(
				FormularioRelatorio.PERIODO_BUSCA
			)?.value as PeriodosLabel;

			const filename = gerarFilenameGrafico(
				`grafico_pluviometria_observada_${agrupamento?.toLocaleLowerCase()}_${periodoAgrupamento?.toLocaleLowerCase()}`
			);

			this.downloadDataUrl(`${filename}.png`, dataUrl);
		} catch (e) {
			console.error('[PLUVIOMETRIA PNG] Erro ao exportar PNG:', e);
		}
	}
}
